import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import Footer from "../../components/Footer";
import "../../css/Flycam.css";

// Sửa đường dẫn này nếu file JSON nằm ở vị trí khác.
const CONFIG_URL =
    "https://raw.githubusercontent.com/VuongMinh01/MinMedia-anh/main/Flycam/flycam.json";

const GITHUB_API =
    "https://api.github.com/repos/VuongMinh01/MinMedia-anh/contents/Flycam/images?ref=main";

const RAW_BASE =
    "https://raw.githubusercontent.com/VuongMinh01/MinMedia-anh/main/Flycam/images";

const imageExtensions = /\.(jpg|jpeg|png|webp|gif)$/i;

function getImageCategory(filename) {
    const name = filename.toLowerCase();

    if (name.startsWith("flycam-chuyennghiep-")) {
        return "chuyennghiep";
    }

    if (name.startsWith("flycam-nho-")) {
        return "nho";
    }

    if (name.startsWith("flycam-anh-")) {
        return "gallery";
    }

    if (name.startsWith("flycam-banner-")) {
        return "banner";
    }

    return "other";
}

function getYoutubeId(value) {
    if (!value) return "";

    try {
        const url = new URL(value);
        const host = url.hostname.toLowerCase().replace(/^www\./, "");

        if (host === "youtu.be") {
            return url.pathname.split("/").filter(Boolean)[0] || "";
        }

        if (
            [
                "youtube.com",
                "m.youtube.com",
                "youtube-nocookie.com",
            ].includes(host)
        ) {
            if (url.pathname === "/watch") {
                return url.searchParams.get("v") || "";
            }

            const match = url.pathname.match(
                /^\/(?:embed|shorts|live)\/([^/]+)/
            );

            return match ? match[1] : "";
        }
    } catch {
        return "";
    }

    return "";
}

function Flycam() {
    const navigate = useNavigate();

    const [config, setConfig] = useState(null);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [configError, setConfigError] = useState("");
    const [filter, setFilter] = useState("all");
    const [selectedImage, setSelectedImage] = useState(null);

    // Đọc nội dung JSON trên GitHub.
    useEffect(() => {
        const controller = new AbortController();

        async function loadConfig() {
            try {
                const response = await fetch(CONFIG_URL, {
                    signal: controller.signal,
                });

                if (!response.ok) {
                    throw new Error(
                        `Không tải được JSON: HTTP ${response.status}. Kiểm tra CONFIG_URL.`
                    );
                }

                const data = await response.json();
                setConfig(data);
            } catch (err) {
                if (err.name !== "AbortError") {
                    setConfigError(
                        err.message || "Không thể tải dữ liệu JSON."
                    );
                }
            }
        }

        loadConfig();

        return () => controller.abort();
    }, []);

    // Tải danh sách hình ảnh trong thư mục GitHub.
    useEffect(() => {
        const controller = new AbortController();

        async function loadImages() {
            try {
                setLoading(true);
                setError("");

                const response = await fetch(GITHUB_API, {
                    signal: controller.signal,
                    headers: {
                        Accept: "application/vnd.github+json",
                    },
                });

                if (!response.ok) {
                    throw new Error(
                        `GitHub trả về lỗi ${response.status}. Kiểm tra repository và thư mục images.`
                    );
                }

                const data = await response.json();

                if (!Array.isArray(data)) {
                    throw new Error(
                        "Dữ liệu thư mục ảnh GitHub không hợp lệ."
                    );
                }

                const images = data
                    .filter(
                        (file) =>
                            file.type === "file" &&
                            imageExtensions.test(file.name)
                    )
                    .map((file) => ({
                        id: file.sha,
                        name: file.name,
                        title: file.name
                            .replace(/\.[^.]+$/, "")
                            .replace(/^flycam-anh-/i, "")
                            .replace(/[-_]+/g, " ")
                            .replace(/\b\w/g, (char) => char.toUpperCase()), src: `${RAW_BASE}/${encodeURIComponent(file.name)}`,
                        category: getImageCategory(file.name),
                    }));

                setFiles(images);
            } catch (err) {
                if (err.name !== "AbortError") {
                    setError(err.message || "Không thể tải ảnh.");
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        }

        loadImages();

        return () => controller.abort();
    }, []);

    const galleryImages = useMemo(
        () => files.filter((file) => file.category === "gallery"),
        [files]
    );

    const filteredImages = useMemo(
        () => galleryImages,
        [galleryImages]
    );

    const equipmentImages = (prefix) =>
        files
            .filter((file) =>
                file.name.toLowerCase().startsWith(
                    (prefix || "").toLowerCase()
                )
            )
            .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, {
                    numeric: true,
                })
            );

    const banner = useMemo(() => {
        const prefix = config?.hero?.bannerPrefix || "flycam-banner-";

        return files.find((file) =>
            file.name.toLowerCase().startsWith(prefix.toLowerCase())
        );
    }, [files, config]);

    const equipment = config?.equipment || [];
    const videos = Array.isArray(config?.videos) ? config.videos : [];

    // Đóng ảnh bằng phím Escape.
    useEffect(() => {
        if (!selectedImage) return;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setSelectedImage(null);
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [selectedImage]);

    return (
        <div className="flycam-page">

            {/* HERO */}
            <section
                className="flycam-hero"
                style={
                    banner
                        ? {
                            backgroundImage: `linear-gradient(90deg, rgba(10,11,9,.9), rgba(10,11,9,.35)), url("${banner.src}")`,
                        }
                        : undefined
                }
            >
                <Container className="flycam-hero-content">
                    <button
                        className="flycam-back-button"
                        onClick={() => navigate(-1)}
                    >
                        ← Quay lại
                    </button>

                    <p className="flycam-eyebrow">
                        {config?.hero?.eyebrow ||
                            "MIN MEDIA • AERIAL PRODUCTION"}
                    </p>

                    <h1>
                        {config?.hero?.title ||
                            "Nhìn thế giới từ một góc nhìn khác."}
                    </h1>

                    <p className="flycam-description">
                        {config?.hero?.description ||
                            "Dịch vụ quay Flycam chuyên nghiệp của Min Media."}
                    </p>

                    <button
                        className="flycam-primary-button"
                        onClick={() => navigate("/contact")}
                    >
                        Liên hệ tư vấn →
                    </button>
                </Container>
            </section>

            {(loading || configError || error) && (
                <div className="flycam-status">
                    {loading && <p>Đang tải hình ảnh từ GitHub...</p>}

                    {configError && (
                        <p className="flycam-error">
                            Lỗi cấu hình: {configError}
                        </p>
                    )}

                    {error && (
                        <p className="flycam-error">{error}</p>
                    )}
                </div>
            )}

            {/* GIỚI THIỆU */}
            <section className="flycam-section flycam-intro">
                <Container>
                    <p className="flycam-eyebrow">
                        {config?.intro?.eyebrow || "VỀ DỊCH VỤ"}
                    </p>

                    <h2>
                        {config?.intro?.title ||
                            "Ghi lại những khoảnh khắc từ trên cao"}
                    </h2>

                    <p>
                        {config?.intro?.description ||
                            "Min Media mang đến góc nhìn toàn cảnh cho dự án của bạn."}
                    </p>
                </Container>
            </section>

            {/* THIẾT BỊ */}
            <section className="flycam-section flycam-equipment">
                <Container>
                    <p className="flycam-eyebrow">THIẾT BỊ</p>
                    <h2>Các dòng Flycam</h2>

                    <Row className="g-4">
                        {equipment.map((item) => {
                            const images = equipmentImages(
                                item.imagePrefix
                            );

                            return (
                                <Col md={6} key={item.id}>
                                    <article className="flycam-equipment-card">
                                        {images.length > 0 ? (
                                            <div className="flycam-equipment-gallery">
                                                {images.map((image) => (
                                                    <button
                                                        type="button"
                                                        key={image.id}
                                                        className="flycam-image-button"
                                                        onClick={() =>
                                                            setSelectedImage(image)
                                                        }
                                                        aria-label={`Xem ${image.title}`}
                                                    >
                                                        <img
                                                            src={image.src}
                                                            alt={image.title}
                                                            loading="lazy"
                                                        />
                                                    </button>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="flycam-placeholder">
                                                Chưa có ảnh thiết bị.
                                                <br />
                                                Tiền tố ảnh:
                                                <br />
                                                <strong>
                                                    {item.imagePrefix}1.jpg
                                                </strong>
                                            </div>
                                        )}

                                        <div className="flycam-card-body">
                                            <h3>{item.title}</h3>

                                            {item.model && (
                                                <p className="flycam-model">
                                                    {item.model}
                                                </p>
                                            )}

                                            <p>{item.description}</p>

                                            <h4>Thông số kỹ thuật</h4>

                                            <dl className="flycam-specs">
                                                {(item.specs || []).map(
                                                    (spec) => (
                                                        <div key={spec.label}>
                                                            <dt>
                                                                {spec.label}
                                                            </dt>
                                                            <dd>
                                                                {spec.value}
                                                            </dd>
                                                        </div>
                                                    )
                                                )}
                                            </dl>
                                        </div>
                                    </article>
                                </Col>
                            );
                        })}
                    </Row>
                </Container>
            </section>

            {/* THƯ VIỆN ẢNH */}
            <section
                className="flycam-section flycam-gallery"
                id="thu-vien"
            >
                <Container>
                    <p className="flycam-eyebrow">TÁC PHẨM THỰC TẾ</p>

                    <h2>
                        {config?.gallery?.title ||
                            "Thư viện hình ảnh Flycam"}
                    </h2>

                    <p>
                        {config?.gallery?.description ||
                            "Những khung hình thực tế của Min Media."}
                    </p>

                    <div className="flycam-filters">
                        <button
                            type="button"
                            className={filter === "all" ? "active" : ""}
                            onClick={() => setFilter("all")}
                        >
                            Tất cả
                        </button>

                        <button
                            type="button"
                            className={filter === "gallery" ? "active" : ""}
                            onClick={() => setFilter("gallery")}
                        >
                            Ảnh dự án
                        </button>
                    </div>

                    {!loading &&
                        !error &&
                        filteredImages.length === 0 && (
                            <div className="flycam-placeholder">
                                Chưa có ảnh thư viện.
                                <br />
                                Hãy tải ảnh có tiền tố:
                                <strong>
                                    {config?.gallery?.imagePrefix ||
                                        "flycam-anh-"}
                                    1.jpg
                                </strong>
                            </div>
                        )}

                    <Row className="g-4">
                        {filteredImages.map((image) => (
                            <Col
                                xs={12}
                                sm={6}
                                lg={4}
                                key={image.id}
                            >
                                <article className="flycam-media-card">
                                    <button
                                        type="button"
                                        className="flycam-image-button"
                                        onClick={() =>
                                            setSelectedImage(image)
                                        }
                                        aria-label={`Phóng to ${image.title}`}
                                    >
                                        <img
                                            src={image.src}
                                            alt={image.title}
                                            loading="lazy"
                                        />
                                    </button>

                                    <div className="flycam-card-body">
                                        <h3>{image.title}</h3>
                                    </div>
                                </article>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* VIDEO YOUTUBE ĐỌC TỪ JSON */}
            {/* VIDEO YOUTUBE */}
            <section
                className="flycam-section flycam-videos"
                id="video"
            >
                <Container>
                    <div className="flycam-video-heading">
                        <div>
                            <p className="flycam-eyebrow">
                                MIN MEDIA FILM
                            </p>

                            <h2>
                                Những thước phim
                                <br />
                                <span>từ trên cao.</span>
                            </h2>
                        </div>

                        <p className="flycam-video-intro">
                            Mỗi góc máy là một góc nhìn khác biệt.
                            Cùng khám phá những khoảnh khắc được ghi lại
                            qua góc nhìn Flycam của Min Media.
                        </p>
                    </div>

                    <div className="flycam-video-list">
                        {videos.map((video, index) => {
                            const videoId = getYoutubeId(video.youtubeUrl);

                            if (!videoId) return null;

                            return (
                                <article
                                    className="flycam-featured-video"
                                    key={video.id}
                                >
                                    <div className="flycam-featured-media">
                                        <div className="flycam-video-frame">
                                            <iframe
                                                src={`https://www.youtube.com/embed/${videoId}`}
                                                title={video.title}
                                                loading="lazy"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                referrerPolicy="strict-origin-when-cross-origin"
                                                allowFullScreen
                                            />
                                        </div>

                                        <span className="flycam-video-index">
                                            FILM / {String(index + 1).padStart(2, "0")}
                                        </span>
                                    </div>

                                    <div className="flycam-featured-info">
                                        <p className="flycam-video-tag">
                                            <span />
                                            VIDEO DỰ ÁN
                                        </p>

                                        <h3>{video.title}</h3>

                                        <p className="flycam-featured-description">
                                            {video.description ||
                                                "Những thước phim ghi lại góc nhìn ấn tượng từ trên cao, được thực hiện bởi Min Media."}
                                        </p>

                                        <div className="flycam-video-divider" />

                                        <div className="flycam-video-meta">
                                            <span>MIN MEDIA</span>
                                            <span>AERIAL PRODUCTION</span>
                                        </div>

                                        <a
                                            className="flycam-youtube-link"
                                            href={`https://www.youtube.com/watch?v=${videoId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Xem trên YouTube
                                            <span aria-hidden="true">↗</span>
                                        </a>
                                    </div>
                                </article>
                            );
                        })}

                        {videos.length === 0 && (
                            <div className="flycam-placeholder">
                                Chưa có video trong file JSON.
                            </div>
                        )}
                    </div>
                </Container>
            </section>

            {/* LIÊN HỆ */}
            <section className="flycam-contact">
                <Container>
                    <p className="flycam-eyebrow">
                        LET'S WORK TOGETHER
                    </p>

                    <h2>
                        {config?.contact?.title ||
                            "Bạn có dự án cần quay Flycam?"}
                    </h2>

                    <p>
                        {config?.contact?.description ||
                            "Liên hệ Min Media để trao đổi về dự án."}
                    </p>

                    <button
                        className="flycam-primary-button"
                        onClick={() =>
                            navigate(config?.contact?.path || "/contact")
                        }
                    >
                        Liên hệ Min Media →
                    </button>
                </Container>
            </section>

            {/* PHÓNG TO ẢNH */}
            {selectedImage && (
                <div
                    className="flycam-lightbox"
                    role="dialog"
                    aria-modal="true"
                    aria-label={selectedImage.title}
                    onClick={() => setSelectedImage(null)}
                >
                    <button
                        type="button"
                        className="flycam-lightbox-close"
                        onClick={() => setSelectedImage(null)}
                        aria-label="Đóng ảnh"
                    >
                        ×
                    </button>

                    <img
                        src={selectedImage.src}
                        alt={selectedImage.title}
                        onClick={(event) => event.stopPropagation()}
                    />

                    <p>{selectedImage.title}</p>
                </div>
            )}

            <Footer />
        </div>
    );
}

export default Flycam;